import React, { useMemo, memo, useEffect, useReducer, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Text, Advice, createUseStyles } from '@Sage/hoi-poi-ui';

import { useWeb5Context } from 'lib/Web5Context';
import {
    getFieldValidation,
    createFieldValidation,
    updateFieldValidation,
} from 'lib/services/fieldsValidations';
import { getValidationsTypesList } from 'lib/literals';
import { showErrorToast } from 'lib/errors';
import { successToast } from 'utils/toast';

import ForceForm, { validate } from 'components/ForceForm';
import HyperDrawer from 'components/HyperDrawer';

import styles from './styles';
const useStyles = createUseStyles(styles, 'FormFieldsValidations');

const initialState = {
    data: {},
    originalData: {},
    fields: [],
    crudType: null,
    errors: {},
    isOpen: false,
    isLoading: true,
    isSaving: false,
    isReadOnly: null,
};

function reducer(state, action) {
    switch (action.type) {
        case 'init':
            return {
                ...state,
                originalData: action.data,
                data: action.data,
                isReadOnly: action.isReadOnly,
                isDuplicate: action.isDuplicate,
                errors: {},
                isOpen: true,
                isLoading: false,
            };
        case 'close':
            return { ...state, isSaving: false, isOpen: false };
        case 'changeData':
            return { ...state, originalData: { ...state.originalData }, data: action.data };
        case 'setErrors':
            return { ...state, isSaving: false, errors: action.errors };
        case 'setSaving':
            return {
                ...state,
                isSaving: action.value,
            };
        default:
            throw new Error('No action provided');
    }
}

const FormFieldsValidations = ({
    onRef,
    entityLogName,
    overrides,
    refreshList = () => {},
    onSave = () => {},
    onStack = () => {},
    setActiveRowId = () => {},
    stackIndex = 0,
}) => {
    const { t, permissions, logEvent } = useWeb5Context({ overrides });
    const [state, dispatch] = useReducer(reducer, initialState);
    const schema = useRef();
    const classes = useStyles();

    // Child method
    useEffect(() => {
        onRef &&
            onRef({
                open({ id, isDuplicate, type }) {
                    const fieldTypesList = getValidationsTypesList(t);
                    const fieldTypesObj = fieldTypesList.reduce(
                        (obj, el) => ({ ...obj, [el.value]: el.label }),
                        {},
                    );
                    const getFieldValidationData = (id) =>
                        id
                            ? getFieldValidation(id)
                            : Promise.resolve({
                                  isStandard: false,
                                  type,
                                  code: `var handler = function (value) {
  return '';
}`,
                              });

                    onStack(stackIndex + 2);

                    getFieldValidationData(id)
                        .then((data) => {
                            const isReadOnly =
                                (data.isStandard && !isDuplicate) ||
                                !permissions?.fieldsValidations?.update;
                            schema.current = {
                                required: ['name', 'description', 'type'],
                                properties: {
                                    name: {
                                        maxLength: 50,
                                        type: ['string'],
                                    },
                                    type: {
                                        type: ['string'],
                                    },
                                    decription: {
                                        maxLength: 200,
                                        type: ['string'],
                                    },
                                    code: {
                                        type: ['string'],
                                    },
                                },
                                layout: {
                                    id: 'validations',
                                    uiGrid: 'linearVertical',
                                    composition: [
                                        {
                                            schemaId: 'name',
                                            uiType: 'text',
                                            label: 'cfm_label_name',
                                            placeholder: 'placeholder_text_field',
                                            isReadOnly,
                                        },
                                        {
                                            schemaId: 'description',
                                            uiType: 'textarea',
                                            label: 'cfm_label_description',
                                            placeholder: 'placeholder_text_field',
                                            isReadOnly,
                                        },
                                        {
                                            schemaId: 'type',
                                            uiType: 'list',
                                            label: 'cfm_label_data_type',
                                            placeholder: 'label_selectone',
                                            options: fieldTypesList,
                                            isReadOnly: isReadOnly || !!type,
                                        },
                                        {
                                            schemaId: 'code',
                                            uiType: 'code',
                                            label: 'cfm_label_code',
                                            isReadOnly,
                                        },
                                    ],
                                },
                            };

                            data.type = data.type
                                ? { value: data.type, label: fieldTypesObj[data.type] }
                                : null;

                            if (isDuplicate) {
                                data.id = null;
                                data.name = '';
                                data.description = '';
                            }

                            dispatch({
                                type: 'init',
                                originalData: data,
                                data,
                                getValidationsTypesList,
                                isReadOnly,
                                isDuplicate,
                            });
                        })
                        .catch((error) => {
                            console.error(error);
                            dispatch({ type: 'setErrors', errors: error });
                            showErrorToast(t, error);
                        });
                },
            });
    }, [onStack, onRef, t, stackIndex, permissions?.fieldsValidations?.update]);

    const handleOnSave = useCallback(() => {
        logEvent({
            event: entityLogName,
            functionality: state.data.id ? 'update' : 'create',
        });

        if (state?.isReadOnly) {
            setActiveRowId(null);
            return dispatch({ type: 'close' });
        }

        const finalData = {
            id: state.data.id,
            name: state.data.name,
            description: state.data.description,
            type: state.data.type?.value || null,
            code: state.data.code,
        };

        validate(finalData, schema.current).then(({ isValid, errors, cleanData }) => {
            if (!isValid) {
                dispatch({ type: 'setErrors', errors });
            } else {
                dispatch({ type: 'setSaving', value: true });

                const file = cleanData.code;
                const saveValidation = (id, data, file) =>
                    id ? updateFieldValidation(id, data, file) : createFieldValidation(data, file);

                delete cleanData.code;

                saveValidation(state.data.id, cleanData, file)
                    .then((data) => {
                        refreshList && refreshList();
                        setActiveRowId(null);
                        dispatch({ type: 'close' });
                        successToast({
                            t,
                            text: state.data.id
                                ? t('cfm_success_update_validation')
                                : t('cfm_success_create_validation'),
                        });
                        onSave(data);
                        onStack(stackIndex - 2);
                    })
                    .catch((error) => {
                        console.error(error);
                        showErrorToast(t, error);
                        dispatch({ type: 'setErrors', errors: error });
                    });
            }
        });
    }, [
        logEvent,
        entityLogName,
        state.data.id,
        state.data.name,
        state.data.description,
        state.data.type?.value,
        state.data.code,
        state?.isReadOnly,
        setActiveRowId,
        refreshList,
        t,
        onSave,
        onStack,
        stackIndex,
    ]);

    const locales = useMemo(() => {
        if (state?.isReadOnly) {
            return {
                title: state?.data?.name || '',
                cancel: null,
                save: t('close'),
            };
        } else {
            return {
                title: state.data.id
                    ? t('cfm_title_update_validation')
                    : t('cfm_title_create_validation'),
                cancel: t('cfm_action_cancel'),
                save: t('cfm_action_save'),
            };
        }
    }, [state.data, state?.isReadOnly, t]);

    const handleOnCancel = useCallback(() => {
        setActiveRowId(null);
        dispatch({ type: 'close' });
        onStack(stackIndex - 2);
    }, [setActiveRowId, onStack, stackIndex]);

    const onChange = useCallback((data, field) => {
        if (field === 'name') {
            const name = data.name?.replace(/\\|\//g, '');
            let finalData = {
                ...data,
                name,
            };
            return dispatch({ type: 'changeData', data: finalData });
        }
        return dispatch({ type: 'changeData', data });
    }, []);

    return (
        <HyperDrawer
            isOpen={state.isOpen}
            locales={locales}
            isLoading={state.isLoading}
            isSaving={state.isSaving}
            onCancel={handleOnCancel}
            onSave={handleOnSave}
            data={state.data}
            originalData={state.originalData}
            className="fields-validation__form"
            size="large"
        >
            <ForceForm
                values={state.data}
                errors={state.errors}
                onChange={onChange}
                schema={schema.current}
                mode="create"
                className={classes.root}
            />
            {state.data?.associatedFields &&
                !!Object.keys(state.data?.associatedFields).length &&
                !state.isDuplicate && (
                    <Advice
                        className={classes.adviceRoot}
                        title={
                            <Text className={classes.adviceTitle} bold>
                                {t('cfm_label_related_entities')}
                            </Text>
                        }
                        type="info"
                        showIcon
                    >
                        {Object.keys(state.data.associatedFields).map((key, index) => (
                            <div key={key} className={classes.entity}>
                                {index > 0 && <div className={classes.divider}></div>}
                                <Text className={classes.entityTitle} bold>
                                    {key}
                                </Text>
                                {state.data.associatedFields[key].map((field) => (
                                    <Text key={field} className={classes.field}>
                                        {t(field)}
                                    </Text>
                                ))}
                            </div>
                        ))}
                    </Advice>
                )}
        </HyperDrawer>
    );
};

FormFieldsValidations.propTypes = {
    onRef: PropTypes.func.isRequired,
    refreshList: PropTypes.func,
    onSave: PropTypes.func,
    onCancel: PropTypes.func,
    setActiveRowId: PropTypes.func,
    stackIndex: PropTypes.number,
};

export default memo(FormFieldsValidations);
